import {
  getDokumentTypLabel,
  dokumentDisplayNumber,
  effectiveBelegdatum,
  formatBelegdatum,
  getDokumentListStatusBadge,
  getDokumentListStatusLabel,
  isHeroOriginDokument,
  RECHNUNG_ART_LABELS,
  ZAHLUNGSSTATUS_LABELS,
  type DokumentListItem,
  type DokumentZahlungEintrag,
  type DokumentZahlungProtokoll,
  type RechnungArt,
  type Zahlungsstatus,
} from "@/lib/dokumente";
import { formatCurrency } from "@/lib/documentCalc";
import {
  PROJECT_WORKFLOW_STATUS_LABELS,
  normalizeProjectWorkflowStatus,
  type ProjectWorkflowStatusKey,
} from "@/lib/projects";

export const DOCUMENT_REQUIRED_COLUMNS = ["dokument_nummer"] as const;

/** Dokument-eigene Spalten. */
export const DOCUMENT_NATIVE_COLUMNS = [
  "dokument_nummer",
  "dokument_typ",
  "rechnung_art",
  "status",
  "zahlungsstatus",
  "zahlung_erfasst",
  "kunde",
  "betreff",
  "belegdatum",
  "gueltig_bis",
  "versendet_am",
  "beauftragt_am",
  "faelligkeitsdatum",
  "bestellnummer",
  "summe_netto",
  "summe_steuer",
  "summe_brutto",
  "summe_bezahlt",
  "summe_offen",
  "waehrung",
  "created_by_name",
  "created_at",
  "updated_at",
] as const;

/** Projektfelder (aus Auftrag am Dokument). */
export const DOCUMENT_PROJECT_COLUMNS = [
  "projekt",
  "auftrag_nummer",
  "auftrag_beschreibung",
  "auftrag_bestellnummer",
  "auftrag_kundenreferenz",
  "auftrag_leitweg_id",
  "leitweg_id",
  "auftrag_ansprechpartner",
  "auftrag_ort",
  "auftrag_ort_zusatz",
  "auftrag_ordnernummer",
  "auftrag_jahr",
  "auftrag_workflow_status",
] as const;

export const DOCUMENT_COLUMNS = [
  ...DOCUMENT_NATIVE_COLUMNS,
  ...DOCUMENT_PROJECT_COLUMNS,
] as const;

export type DocumentColumnKey = (typeof DOCUMENT_COLUMNS)[number];

export const DOCUMENT_COLUMN_LABELS: Record<DocumentColumnKey, string> = {
  dokument_nummer: "Nummer",
  dokument_typ: "Typ",
  rechnung_art: "Rechnungsart",
  status: "Status",
  zahlungsstatus: "Zahlungsstatus",
  zahlung_erfasst: "Zahlungsverlauf",
  kunde: "Kunde",
  betreff: "Betreff",
  belegdatum: "Datum",
  gueltig_bis: "Gültig bis",
  versendet_am: "Versendet am",
  beauftragt_am: "Beauftragt am",
  faelligkeitsdatum: "Fällig am",
  bestellnummer: "Bestellnummer",
  leitweg_id: "Leitweg-ID",
  summe_netto: "Netto",
  summe_steuer: "Steuer",
  summe_brutto: "Brutto",
  summe_bezahlt: "Bezahlt",
  summe_offen: "Offen",
  waehrung: "Währung",
  created_by_name: "Erstellt von",
  created_at: "Erstellt am",
  updated_at: "Geändert am",
  projekt: "Projekt",
  auftrag_nummer: "Projektnummer",
  auftrag_beschreibung: "Projektbeschreibung",
  auftrag_bestellnummer: "Projekt-Bestellnummer",
  auftrag_kundenreferenz: "Kundenreferenz",
  auftrag_leitweg_id: "Projekt-Leitweg-ID",
  auftrag_ansprechpartner: "Ansprechpartner",
  auftrag_ort: "Ort",
  auftrag_ort_zusatz: "Ort Zusatz",
  auftrag_ordnernummer: "Ordner",
  auftrag_jahr: "Jahr",
  auftrag_workflow_status: "Projektstatus",
};

/** Standard-Sicht (wie bisher); weitere Spalten sind wählbar. */
export const DOCUMENT_DEFAULT_COLUMNS: DocumentColumnKey[] = [
  "dokument_nummer",
  "dokument_typ",
  "status",
  "kunde",
  "projekt",
  "belegdatum",
  "summe_brutto",
];

export const DOCUMENT_HIDDEN_BY_DEFAULT_COLUMNS: DocumentColumnKey[] = DOCUMENT_COLUMNS.filter(
  (col) => !DOCUMENT_DEFAULT_COLUMNS.includes(col),
);

export const DOCUMENT_COL_MIN_WIDTH = 72;
export const DOCUMENT_COL_MAX_WIDTH = 480;

const DEFAULT_WIDTHS: Partial<Record<DocumentColumnKey, number>> = {
  dokument_nummer: 148,
  dokument_typ: 140,
  rechnung_art: 120,
  status: 120,
  zahlungsstatus: 120,
  zahlung_erfasst: 220,
  kunde: 180,
  betreff: 200,
  belegdatum: 110,
  gueltig_bis: 110,
  versendet_am: 120,
  beauftragt_am: 120,
  faelligkeitsdatum: 110,
  bestellnummer: 130,
  leitweg_id: 160,
  summe_netto: 110,
  summe_steuer: 100,
  summe_brutto: 110,
  summe_bezahlt: 110,
  summe_offen: 110,
  waehrung: 80,
  created_by_name: 140,
  created_at: 120,
  updated_at: 120,
  projekt: 200,
  auftrag_nummer: 140,
  auftrag_beschreibung: 220,
  auftrag_bestellnummer: 130,
  auftrag_kundenreferenz: 140,
  auftrag_leitweg_id: 160,
  auftrag_ansprechpartner: 160,
  auftrag_ort: 140,
  auftrag_ort_zusatz: 140,
  auftrag_ordnernummer: 100,
  auftrag_jahr: 80,
  auftrag_workflow_status: 140,
};

export function getAvailableDocumentColumns(): DocumentColumnKey[] {
  return [...DOCUMENT_COLUMNS];
}

export function getDefaultDocumentColumns(): DocumentColumnKey[] {
  return [...DOCUMENT_DEFAULT_COLUMNS];
}

export function normalizeDocumentColumnSelection(
  selected: string[] | null | undefined,
  available: DocumentColumnKey[] = getAvailableDocumentColumns(),
): DocumentColumnKey[] {
  const allowed = new Set(available);
  const out: DocumentColumnKey[] = [];
  for (const col of selected || []) {
    if (allowed.has(col as DocumentColumnKey) && !out.includes(col as DocumentColumnKey)) {
      out.push(col as DocumentColumnKey);
    }
  }
  for (const required of DOCUMENT_REQUIRED_COLUMNS) {
    if (!out.includes(required)) out.unshift(required);
  }
  if (out.length === 0) return getDefaultDocumentColumns();
  return out;
}

export function getDocumentColumnLabel(col: string): string {
  return DOCUMENT_COLUMN_LABELS[col as DocumentColumnKey] || col;
}

export function getDocumentColumnDefaultWidth(col: string): number {
  return DEFAULT_WIDTHS[col as DocumentColumnKey] ?? 140;
}

export function clampDocumentColumnWidth(width: number): number {
  return Math.min(DOCUMENT_COL_MAX_WIDTH, Math.max(DOCUMENT_COL_MIN_WIDTH, Math.round(width)));
}

export interface DocumentCellValue {
  value: string;
  alignRight?: boolean;
  mono?: boolean;
  kind?: "status";
  badgeClass?: string;
  /** Zusätzliche Textklassen für den Zellwert (z. B. grün bei bezahlter Rechnung). */
  valueClass?: string;
  /** Herkunftsmarkierung neben der Nummer (Hero vs. in der App erstellt). */
  source?: "hero" | null;
  /** Mehrzeiliger Zellinhalt, z. B. mehrere Zahlungseinträge. */
  detailLines?: string[];
  /** Tooltip, falls er vom sichtbaren Text abweicht. */
  title?: string;
}

export interface ZahlungErfasstLine {
  text: string;
  title: string;
}

function formatDateTime(value?: string | null): string {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const ZAHLUNG_AKTION_LABEL: Record<DokumentZahlungProtokoll["aktion"], string> = {
  erfasst: "Erfasst",
  geaendert: "Geändert",
  geloescht: "Gelöscht",
};

export function zahlungProtokollLines(
  entries: DokumentZahlungProtokoll[] | null | undefined,
  currency?: string | null,
): ZahlungErfasstLine[] {
  return (entries || []).map((entry) => {
    const who = (entry.created_by_name || entry.created_by || "").trim() || "Unbekannt";
    const when = formatDateTime(entry.created_at);
    const action = ZAHLUNG_AKTION_LABEL[entry.aktion] || entry.aktion;
    const text = [action, who, when].filter(Boolean).join(" · ");
    const amount =
      entry.betrag != null && !Number.isNaN(Number(entry.betrag))
        ? formatCurrency(Number(entry.betrag), currency || "EUR")
        : "";
    const previous =
      entry.vorher_betrag != null && !Number.isNaN(Number(entry.vorher_betrag))
        ? formatCurrency(Number(entry.vorher_betrag), currency || "EUR")
        : "";
    const change =
      entry.aktion === "geaendert" && previous && amount && previous !== amount
        ? `${previous} → ${amount}`
        : amount;
    const paidOn = entry.bezahlt_am ? formatBelegdatum(entry.bezahlt_am) : "";
    const title = [text, change, paidOn ? `Zahlungsdatum ${paidOn}` : ""].filter(Boolean).join(" · ");
    return { text, title };
  });
}

export function zahlungErfasstLines(
  entries: DokumentZahlungEintrag[] | null | undefined,
  currency?: string | null,
): ZahlungErfasstLine[] {
  return (entries || []).map((entry) => {
    const who = (entry.created_by_name || entry.created_by || "").trim() || "Unbekannt";
    const when = formatDateTime(entry.created_at);
    const text = when ? `${who} · ${when}` : who;
    const amount =
      entry.betrag != null && !Number.isNaN(Number(entry.betrag))
        ? formatCurrency(Number(entry.betrag), currency || "EUR")
        : "";
    const paidOn = entry.bezahlt_am ? formatBelegdatum(entry.bezahlt_am) : "";
    const title = [
      who,
      when ? `eingetragen am ${when}` : "",
      amount,
      paidOn ? `Zahlungsdatum ${paidOn}` : "",
    ]
      .filter(Boolean)
      .join(" · ");
    return { text, title };
  });
}

function invoiceWithoutPaymentCapture(row: DokumentListItem): boolean {
  if (row.dokument_typ !== "rechnung") return false;
  if (row.quelle && row.quelle !== "geschaeftsdokument") return false;
  if (row.rechnung_art === "stornorechnung") return false;
  if ((row.zahlung_eintraege || []).length > 0) return false;
  return (
    row.zahlungsstatus === "bezahlt" ||
    row.zahlungsstatus === "teilbezahlt" ||
    row.status === "abgerechnet"
  );
}

export function ensureRechnungPaymentColumn(
  columns: DocumentColumnKey[],
  lockedTyp?: string | null,
): DocumentColumnKey[] {
  if (lockedTyp !== "rechnung" || columns.includes("zahlung_erfasst")) return columns;
  const next = [...columns];
  const at = next.indexOf("status");
  next.splice(at >= 0 ? at + 1 : next.length, 0, "zahlung_erfasst");
  return next;
}

function isRechnungBezahlt(row: DokumentListItem): boolean {
  return (
    row.dokument_typ === "rechnung" &&
    (row.zahlungsstatus === "bezahlt" || row.status === "abgerechnet")
  );
}

function moneyCell(
  amount: number | null | undefined,
  currency: string | undefined,
  opts?: { emphasizePaid?: boolean; row?: DokumentListItem },
): DocumentCellValue {
  if (amount == null || Number.isNaN(Number(amount))) {
    return { value: "", alignRight: true, mono: true };
  }
  return {
    value: formatCurrency(Number(amount), currency),
    alignRight: true,
    mono: true,
    valueClass:
      opts?.emphasizePaid && opts.row && isRechnungBezahlt(opts.row)
        ? "font-semibold text-status-green"
        : undefined,
  };
}

export function renderDocumentCellValue(row: DokumentListItem, col: string): DocumentCellValue {
  switch (col) {
    case "dokument_nummer":
      return {
        value: dokumentDisplayNumber(row),
        mono: true,
        source: isHeroOriginDokument(row) ? "hero" : null,
        valueClass: row.has_newer_revision ? "text-label-tertiary" : undefined,
      };
    case "dokument_typ":
      return { value: getDokumentTypLabel(row) };
    case "rechnung_art": {
      const art = row.rechnung_art as RechnungArt | null | undefined;
      if (row.dokument_typ !== "rechnung" || !art) return { value: "" };
      return { value: RECHNUNG_ART_LABELS[art] || art };
    }
    case "status":
      return {
        value: getDokumentListStatusLabel(row),
        kind: "status",
        badgeClass: getDokumentListStatusBadge(row),
      };
    case "zahlungsstatus": {
      const zs = row.zahlungsstatus as Zahlungsstatus | null | undefined;
      if (!zs) return { value: "" };
      return { value: ZAHLUNGSSTATUS_LABELS[zs] || zs };
    }
    case "zahlung_erfasst": {
      const protocol = zahlungProtokollLines(row.zahlung_protokoll, row.waehrung);
      const lines = protocol.length
        ? protocol
        : zahlungErfasstLines(row.zahlung_eintraege, row.waehrung);
      if (lines.length > 0) {
        const visible = lines.slice(0, 3);
        const rest = lines.length - visible.length;
        const detailLines = visible.map((line) => line.text);
        if (rest > 0) detailLines.push(`+${rest} weitere`);
        return {
          value: detailLines.join("\n"),
          detailLines,
          title: lines.map((line) => line.title).join("\n"),
        };
      }
      if (invoiceWithoutPaymentCapture(row)) {
        return {
          value: "Nicht erfasst",
          valueClass: "text-label-tertiary",
          title: "Für diese Rechnung liegt kein Zahlungseintrag in der App vor.",
        };
      }
      return { value: "" };
    }
    case "kunde":
      return { value: row.kunde_name || row.kunde_stamm_name || "" };
    case "betreff":
      return { value: row.betreff || "" };
    case "belegdatum":
      return { value: formatBelegdatum(effectiveBelegdatum(row)), mono: true };
    case "gueltig_bis":
      return { value: formatBelegdatum(row.gueltig_bis), mono: true };
    case "versendet_am":
      return { value: formatDateTime(row.versendet_am), mono: true };
    case "beauftragt_am":
      return { value: formatDateTime(row.beauftragt_am), mono: true };
    case "faelligkeitsdatum":
      return { value: formatBelegdatum(row.faelligkeitsdatum), mono: true };
    case "bestellnummer":
      return { value: row.bestellnummer || "", mono: true };
    case "leitweg_id":
      return { value: row.leitweg_id || "", mono: true };
    case "summe_netto":
      return moneyCell(row.summe_netto, row.waehrung);
    case "summe_steuer":
      return moneyCell(row.summe_steuer, row.waehrung);
    case "summe_brutto":
      return moneyCell(row.summe_brutto, row.waehrung, { emphasizePaid: true, row });
    case "summe_bezahlt":
      return moneyCell(row.summe_bezahlt, row.waehrung);
    case "summe_offen":
      return moneyCell(row.summe_offen, row.waehrung);
    case "waehrung":
      return { value: row.waehrung || "" };
    case "created_by_name":
      return { value: row.created_by_name || row.created_by || "" };
    case "created_at":
      return { value: formatDateTime(row.created_at), mono: true };
    case "updated_at":
      return { value: formatDateTime(row.updated_at), mono: true };
    case "projekt":
      return {
        value: [row.auftrag_nummer, row.auftrag_ort].filter(Boolean).join(" · "),
      };
    case "auftrag_nummer":
      return { value: row.auftrag_nummer || "", mono: true };
    case "auftrag_beschreibung":
      return { value: row.auftrag_beschreibung || "" };
    case "auftrag_bestellnummer":
      return { value: row.auftrag_bestellnummer || "", mono: true };
    case "auftrag_kundenreferenz":
      return { value: row.auftrag_kundenreferenz || "", mono: true };
    case "auftrag_leitweg_id":
      return { value: row.auftrag_leitweg_id || "", mono: true };
    case "auftrag_ansprechpartner":
      return { value: row.auftrag_ansprechpartner || "" };
    case "auftrag_ort":
      return { value: row.auftrag_ort || "" };
    case "auftrag_ort_zusatz":
      return { value: row.auftrag_ort_zusatz || "" };
    case "auftrag_ordnernummer":
      return { value: row.auftrag_ordnernummer || "", mono: true };
    case "auftrag_jahr":
      return {
        value: row.auftrag_jahr != null ? String(row.auftrag_jahr) : "",
        mono: true,
      };
    case "auftrag_workflow_status": {
      if (!row.auftrag_workflow_status && !row.auftrag_id) return { value: "" };
      const key = normalizeProjectWorkflowStatus(row.auftrag_workflow_status);
      return {
        value: PROJECT_WORKFLOW_STATUS_LABELS[key as ProjectWorkflowStatusKey] || key,
      };
    }
    default:
      return { value: "" };
  }
}
